import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  ExperienceAnchor,
  ExperienceAnchorType,
  IExperience,
  IExperienceTemplate,
  IItemTemplateTiming,
  IItemTemplateTimingDataChange,
  IItemTemplateTimingDataChangeTask,
  IItemTemplateTimingFixed,
  IItemTemplateTimingFixedTask,
  IItemTemplateTimingTriggered,
  IItemTemplateTimingTriggeredTask,
  isEventDrivenTiming,
  ITaskTemplate
} from '@dadohr/shared/domainTypes/experience';
import { Timestamp } from '@dadohr/shared/domainTypes/time';
import { compact, flatten, keyBy, uniq } from 'lodash';
import { addToTs, fromMoment, ONE_DAY, toMoment } from '../time';
import {
  getAutomationTemplateById,
  getMessageTemplateById,
  getTaskTemplateById
} from './template';

export const getAnchorOffsetForExperience = (
  expTplAnchor: ExperienceAnchor
) => {
  // if the template has an anchor
  // determine the distance of the task to the anchor in the template
  if (expTplAnchor.type === ExperienceAnchorType.EMPLOYEE_DATE) {
    if (expTplAnchor.offset.type === 'ABSOLUTE') {
      return -expTplAnchor.offset.duration;
    } else {
      console.error('UNHANDLED ANCHOR OFFSET TYPE', expTplAnchor.offset.type);
      throw new Error('UNHANDLED ANCHOR OFFSET TYPE');
    }
  }

  // anchor date on the instance should be the start date here - if it's not we have a problem.
  return 0;
};

export const getAllTriggerAncestorsTasks = (
  dependentOn: string[],
  allTasks: ITaskTemplate[]
): ITaskTemplate[] => {
  const tasksById = keyBy(allTasks, (t) => t.id);
  const _get = (
    res: ITaskTemplate[],
    nextParents: string[]
  ): ITaskTemplate[] => {
    const parents = compact(nextParents.map((p) => tasksById[p]));
    res.push(...parents);
    const otherAncestors = uniq(
      flatten(
        parents.map((p) =>
          p.timing.type === 'triggered'
            ? p.timing.dependentOn.map((d) => d.itemId)
            : []
        )
      )
    );

    // TODO Triggered items - beware of cyclical dependencies - add a very naive brake for now.
    if (
      otherAncestors.length &&
      otherAncestors.some((x) => !!res.find((y) => x === y.id))
    ) {
      console.log('potential cyclical trigger found', {
        res,
        otherAncestors,
        allTasks
      });
      return uniq(res);
    }

    if (otherAncestors.length) {
      return _get(res, otherAncestors);
    } else {
      return uniq(res);
    }
  };

  return _get([], dependentOn);
};

export const getApproximateParentTimingForTriggeredItem = (
  timing: IItemTemplateTimingTriggered,
  allTasks: ITaskTemplate[]
) => {
  // this is problematic for chains - do we add up deadlines there?
  // makes everything quite complicated
  const parents = getAllTriggerAncestorsTasks(
    timing.dependentOn.map((x) => x.itemId),
    allTasks
  );
  let parentTiming: IItemTemplateTimingFixedTask = {
    type: 'fixed',
    start: 0,
    end: 0,
    time: null
  };
  parents.forEach((p) => {
    if (p.timing.type === 'fixed' && p.timing.start >= parentTiming.start) {
      parentTiming = p.timing;
    }
  });
  return parentTiming;
};

const tryApplyStartTime = (
  ts: Timestamp,
  startTime: { hour: number; minute: number } | null,
  tz: string
) => {
  if (!startTime) {
    return ts;
  }
  const m = toMoment(ts).tz(tz).hour(startTime.hour).minute(startTime.minute);
  return fromMoment(m);
};

export const getFreshNonTaskStartDate = (
  timing: IItemTemplateTimingFixed,
  instanceAnchorDate: Timestamp,
  expTplAnchor: ExperienceAnchor,
  tz: string
) => {
  const anchorOffset = getAnchorOffsetForExperience(expTplAnchor);
  const msgOffsetFromAnchor = timing.start - anchorOffset;
  return tryApplyStartTime(
    addToTs(instanceAnchorDate, msgOffsetFromAnchor),
    timing.time,
    tz
  );
};

export const getFreshTaskDates = (
  timing: IItemTemplateTimingFixedTask,
  instanceAnchorDate: Timestamp,
  expTplAnchor: ExperienceAnchor,
  tz: string
): {
  start: Timestamp;
  end: Timestamp;
} => {
  const anchorOffset = getAnchorOffsetForExperience(expTplAnchor);
  const duration = timing.end - timing.start;
  const taskOffsetFromAnchor = timing.start - anchorOffset;
  const start = tryApplyStartTime(
    addToTs(instanceAnchorDate, taskOffsetFromAnchor),
    timing.time,
    tz
  );

  return {
    start,
    end: addToTs(start, duration)
  };
};

export const isStandardTimingForTask = (
  tpl: Doc<IExperienceTemplate>,
  exp: Doc<IExperience>,
  taskTplId: string,
  timing: { start: Timestamp; end: Timestamp }
) => {
  const taskTpl = getTaskTemplateById(tpl, taskTplId);
  if (!taskTpl) {
    console.warn('message template not found', { tpl, taskTplId, exp });
    return false;
  }
  if (isEventDrivenTiming(taskTpl.timing)) {
    return false;
  }
  const standardTiming = getFreshTaskDates(
    taskTpl.timing,
    exp.data.anchorDate.at,
    tpl.data.anchor,
    tpl.data.tz
  );
  return (
    standardTiming.start.isEqual(timing.start) &&
    standardTiming.end.isEqual(timing.end)
  );
};

export const isStandardTimingForMessage = (
  tpl: Doc<IExperienceTemplate>,
  exp: Doc<IExperience>,
  msgTplId: string,
  timing: Timestamp
) => {
  const msgTpl = getMessageTemplateById(tpl, msgTplId);
  if (!msgTpl) {
    console.warn('message template not found', { tpl, msgTplId, exp });
    return false;
  }
  if (isEventDrivenTiming(msgTpl.timing)) {
    return false;
  }
  const standardTiming = getFreshNonTaskStartDate(
    msgTpl.timing,
    exp.data.anchorDate.at,
    tpl.data.anchor,
    tpl.data.tz
  );
  return standardTiming.isEqual(timing);
};

export const isStandardTimingForAutomation = (
  tpl: Doc<IExperienceTemplate>,
  exp: Doc<IExperience>,
  autTplId: string,
  timing: Timestamp
) => {
  const autTpl = getAutomationTemplateById(tpl, autTplId);
  if (!autTpl) {
    console.warn('automation template not found', { tpl, autTplId, exp });
    return false;
  }
  if (isEventDrivenTiming(autTpl.timing)) {
    return false;
  }
  const standardTiming = getFreshNonTaskStartDate(
    autTpl.timing,
    exp.data.anchorDate.at,
    tpl.data.anchor,
    tpl.data.tz
  );
  return standardTiming.isEqual(timing);
};

export const getFixedStartOrInfinity = (timing: IItemTemplateTiming) => {
  if (isEventDrivenTiming(timing)) {
    return Infinity;
  }
  return timing.start;
};

export const isFixedTaskTiming = (
  x: IItemTemplateTimingFixed | IItemTemplateTimingFixedTask
): x is IItemTemplateTimingFixedTask => {
  return (x as any).end !== undefined;
};

export const getFixedTimingOrApproximateParentTiming = (
  timing: IItemTemplateTiming,
  tasks: ITaskTemplate[]
): IItemTemplateTimingFixed | IItemTemplateTimingFixedTask => {
  if (timing.type === 'fixed') {
    return timing;
  }
  if (timing.type === 'dataChange') {
    const start = timing.earliestStart ?? 0;
    return { type: 'fixed', start, time: timing.time };
  }
  return getApproximateParentTimingForTriggeredItem(timing, tasks);
};

export const getFixedTimingOrApproximateParentTimingForTask = (
  timing:
    | IItemTemplateTimingFixedTask
    | IItemTemplateTimingTriggeredTask
    | IItemTemplateTimingDataChangeTask,
  tasks: ITaskTemplate[]
): IItemTemplateTimingFixedTask => {
  if (timing.type === 'fixed') {
    return timing;
  }
  if (timing.type === 'dataChange') {
    const start = timing.earliestStart ?? 0;
    return { type: 'fixed', start, end: start + timing.end.duration, time: timing.time };
  }
  return getApproximateParentTimingForTriggeredItem(timing, tasks);
};

export const isValidTiming = (timing: IItemTemplateTiming) => {
  if (timing.type === 'fixed') {
    // A negative start would fire before the experience begins; the builder
    // can produce one when "N days before milestone" exceeds the offset.
    return timing.start >= 0;
  }
  if (timing.type === 'dataChange') {
    return true;
  }
  return (
    !!timing.dependentOn.length && timing.dependentOn.every((x) => x.itemId)
  );
};

export const getPresumedStartPositionEvaluatingTriggerChains = (
  dependentOn: string[],
  otherTasks: ITaskTemplate[]
) => {
  // This approach is going to lead to problems once we have multiple causes,
  // as the chains are not treated separately from one another!
  const parents = getAllTriggerAncestorsTasks(dependentOn, otherTasks);

  let start = 0;
  parents.forEach((p) => {
    if (p.timing.type === 'fixed') {
      start += p.timing.end;
    } else {
      start += p.timing.end.duration;
    }
  });

  return start;
};

export const getTimelinePositionForTaskTemplate = (
  originalTiming:
    | IItemTemplateTimingFixedTask
    | IItemTemplateTimingTriggeredTask
    | IItemTemplateTimingDataChangeTask,
  otherTasks: ITaskTemplate[]
) => {
  if (originalTiming.type === 'fixed') {
    return {
      start: originalTiming.start,
      end: originalTiming.end
    };
  }

  if (originalTiming.type === 'dataChange') {
    return {
      start: originalTiming.earliestStart ?? 0,
      end: (originalTiming.earliestStart ?? 0) + originalTiming.end.duration
    };
  }

  const start = getPresumedStartPositionEvaluatingTriggerChains(
    originalTiming.dependentOn.map((x) => x.itemId),
    otherTasks
  );

  return {
    start,
    end: start + originalTiming.end.duration
  };
};

export const DEFAULT_MESSAGE_TIMELINE_LENGTH_IN_DAYS = 5;
export const getTimelinePositionForMessageTempate = (
  originalTiming: IItemTemplateTiming,
  otherTasks: ITaskTemplate[]
) => {
  const duration = ONE_DAY * DEFAULT_MESSAGE_TIMELINE_LENGTH_IN_DAYS;

  if (originalTiming.type === 'fixed') {
    return {
      start: originalTiming.start,
      end: originalTiming.start + duration
    };
  }

  if (originalTiming.type === 'dataChange') {
    const start = originalTiming.earliestStart ?? 0;
    return {
      start,
      end: start + duration
    };
  }

  const start = getPresumedStartPositionEvaluatingTriggerChains(
    originalTiming.dependentOn.map((x) => x.itemId),
    otherTasks
  );

  return {
    start,
    end: start + duration // Messages have no end but ok
  };
};

export const DEFAULT_AUTOMATION_TIMELINE_LENGTH_IN_DAYS = 5;
export const getTimelinePositionForAutomationTemplate = (
  originalTiming: IItemTemplateTiming,
  otherTasks: ITaskTemplate[]
) => {
  const duration = ONE_DAY * DEFAULT_AUTOMATION_TIMELINE_LENGTH_IN_DAYS;

  if (originalTiming.type === 'fixed') {
    return {
      start: originalTiming.start,
      end: originalTiming.start + duration
    };
  }

  if (originalTiming.type === 'dataChange') {
    const start = originalTiming.earliestStart ?? 0;
    return {
      start,
      end: start + duration
    };
  }

  const start = getPresumedStartPositionEvaluatingTriggerChains(
    originalTiming.dependentOn.map((x) => x.itemId),
    otherTasks
  );

  return {
    start,
    end: start + duration
  };
};
export const getFixedOrConvertToTaskFixedTimingConsideringTriggerChain = (
  originalTiming:
    | IItemTemplateTimingTriggeredTask
    | IItemTemplateTimingFixedTask
    | IItemTemplateTimingDataChangeTask,
  tpl: IExperienceTemplate
): IItemTemplateTimingFixedTask => {
  if (originalTiming.type === 'fixed') {
    return originalTiming;
  }

  if (originalTiming.type === 'dataChange') {
    const { start, end } = getTimelinePositionForTaskTemplate(
      originalTiming,
      tpl.tasks
    );
    return {
      type: 'fixed',
      start,
      end,
      time: originalTiming.time
    };
  }

  return convertToTaskFixedTimingConsideringTriggerChain(originalTiming, tpl);
};

export const convertToTaskFixedTimingConsideringTriggerChain = (
  originalTiming: IItemTemplateTimingTriggeredTask,
  tpl: IExperienceTemplate
): IItemTemplateTimingFixedTask => {
  const { start, end } = getTimelinePositionForTaskTemplate(
    originalTiming,
    tpl.tasks
  );
  const parentWithFixedTime = getAllTriggerAncestorsTasks(
    originalTiming.dependentOn.map((d) => d.itemId),
    tpl.tasks
  )[0].timing as IItemTemplateTimingFixedTask;
  const updatedTimming: IItemTemplateTimingFixedTask = {
    ...parentWithFixedTime,
    start,
    end
  };
  return updatedTimming;
};

export const getFixedOrConvertToMessageFixedTimingConsideringTriggerChain = (
  originalTiming:
    | IItemTemplateTimingTriggered
    | IItemTemplateTimingFixed
    | IItemTemplateTimingDataChange,
  tpl: IExperienceTemplate
): IItemTemplateTimingFixed => {
  if (originalTiming.type === 'fixed') {
    return originalTiming;
  }

  if (originalTiming.type === 'dataChange') {
    const { start } = getTimelinePositionForMessageTempate(
      originalTiming,
      tpl.tasks
    );
    return {
      type: 'fixed',
      start,
      time: originalTiming.time
    };
  }

  return convertToMessageFixedTimingConsideringTriggerChain(
    originalTiming,
    tpl
  );
};

export const convertToMessageFixedTimingConsideringTriggerChain = (
  originalTiming: IItemTemplateTimingTriggered,
  tpl: IExperienceTemplate
): IItemTemplateTimingFixed => {
  const { start } = getTimelinePositionForMessageTempate(
    originalTiming,
    tpl.tasks
  );
  const parentWithFixedTime = getAllTriggerAncestorsTasks(
    originalTiming.dependentOn.map((d) => d.itemId),
    tpl.tasks
  )[0].timing as IItemTemplateTimingFixedTask;
  const updatedTimming: IItemTemplateTimingFixed = {
    ...parentWithFixedTime,
    start
  };
  return updatedTimming;
};
